import React, { useState, useEffect, useContext } from 'react';
import { createPanelStyle, listPanelStyle } from './OffAirCostsStyles';
import CreateOffAirCost from './CreateOffAirCost';
import CurrentOffAirCosts from './CurrentOffAirCosts';
import {
    GetOffairTypes,
    CreateOffair,
    UpdateOffair,
    GetOffairActivities,
    GetOffairActivityHistories
} from '../../../services/CampaignsDataService';
import { GetEmptyOffAirCost, GetOffAirCostForBackend, mdFormat } from './OffAirCostsUtils';
import { pageContainerStyle } from '../CampaignStyles';
import { AirtimePlanningContext } from '../../../contexts/AirtimePlanningContext';
import moment from 'moment';
import { useModal } from '@adserve/adserve-react-components';
import { OffairAllocatedType } from '../../../constants/Enums';

let mounted = false;

const OffAirCosts = ({ campaign, readOnly, billingAccountId, campaignStartDate, campaignEndDate, revenueTypes = [], recalculateOffAirs  }) => {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const returnStartDate = () => {
        const startDate = moment(campaign.startDate, mdFormat).toDate();
        if (firstDayOfMonth > startDate) return firstDayOfMonth;
        else return startDate;
    };

    const returnEndDate = () => {
        const endDate = moment(campaign.endDate, mdFormat).toDate();
        if (firstDayOfMonth > endDate) return firstDayOfMonth;
        else return endDate;
    };

    const returnBillingDate = () => {
        const now = new Date();
        const lastDayOfCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        const campaignStartMonth = moment(campaign.startDate, mdFormat).month() + 1;
        const campaignStartYear = moment(campaign.startDate, mdFormat).year();
        const lastDayOfCampaignMonth = new Date(campaignStartYear, campaignStartMonth, 0);
        if (lastDayOfCurrentMonth > lastDayOfCampaignMonth) return lastDayOfCurrentMonth;
        else return lastDayOfCampaignMonth;
    };

    // State
    const [offairTypes, setOffairTypes] = useState([]);
    const [currentOffairCost, setCurrentOffairCost] = useState(
        GetEmptyOffAirCost(
            campaign.id,
            returnStartDate(),
            returnEndDate(),
            returnBillingDate(),
            campaign.commissionRate
        )
    );

    const [offairsLoader, setOffairsLoader] = useState(true);
    const { offairActivities, setOffairActivities, setOffairChanges, currentCampaignVersionId } =
        useContext(AirtimePlanningContext);
    const { addMessage } = useModal();
    const apiParamDefaults = {
        sortField: 'createdDate',
        sortOrder: 'asc',
        pageNumber: 1,
        pageSize: 5000
    };
    const [currentRevenueType, setCurrentRevenueType] = useState(revenueTypes?.find((rt) => rt?.id === campaign?.revenueTypeId));

    const getOffairTypes = async () => {
        let data = [];
        try {
            data = await GetOffairTypes();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err.response?.data ?? err.message ?? err);
            addMessage({
                message: window.translate('An error occurred while attempting to retrieve Off-Air Types.'),
                buttons: [
                    { text: window.translate('Retry'), callback: getOffairTypes },
                    { text: window.translate('Cancel') }
                ]
            });
        }
        const defaultOffAirType = data.find((ot) => ot.isDefault);
        if (!mounted) {
            return;
        }
        setOffairTypes(data);
        if (defaultOffAirType && currentOffairCost.offairTypeId === window.emptyGuid) {
            setCurrentOffairCost({ ...currentOffairCost, offairTypeId: defaultOffAirType.id });
        }
    };

    useEffect(() => {
        mounted = true;
        getOffairTypes();
        return () => {
            mounted = false;
        };
    }, []);

    const loadData = () => {
        if (currentCampaignVersionId === window.emptyGuid) {
            GetOffairActivities(campaign.id, apiParamDefaults)
                .then((data) => {
                    for (const row of data) {
                        row.proRateMonthly = row.allocatedType === OffairAllocatedType.MONTHLY;
                        row.proDataDays = row.allocatedType === OffairAllocatedType.DAYS;
                    }
                    setOffairActivities(data);
                    campaign.offairActivities = data;
                })
                // eslint-disable-next-line no-console
                .catch(console.log);
        } else {
            GetOffairActivityHistories(campaign.id, currentCampaignVersionId, apiParamDefaults)
                .then((data) => {
                    for (const row of data) {
                        row.proRateMonthly = row.allocatedType === OffairAllocatedType.MONTHLY;
                        row.proDataDays = row.allocatedType === OffairAllocatedType.DAYS;
                    }
                    setOffairActivities(data);
                    campaign.offairActivities = data;
                })
                // eslint-disable-next-line no-console
                .catch(console.log);
        }
    };

    useEffect(() => {
        loadData();
    }, [campaign.id, offairsLoader, currentCampaignVersionId, campaign.campaignVersions]);

    const updateCurrentOffairCost = (name, value) => {
        setCurrentOffairCost((data) => ({ ...data, [name]: value }));
    };

    const handleChange = (event) => {
        updateCurrentOffairCost(event.target.name, event.target.value);
    };

    const handleChangeByName = (name, value) => {
        updateCurrentOffairCost(name, value);
    };

    const handleSave = async () => {
        const offAirs = await GetOffAirCostForBackend(currentOffairCost);
        setOffairsLoader(true);

        for (const data of offAirs) {
            if (currentOffairCost.id === window.emptyGuid) {
                CreateOffair(data).then((res) => {
                    setOffairsLoader(false);
                    campaign.offairActivities.push(res);
                    setOffairChanges(true);
                });
            } else {
                UpdateOffair(data).then((res) => {
                    setOffairsLoader(false);
                    setOffairChanges(true);
                });
            }
        }
        campaign.modified = true;
        handleClear();
    };

    const setInitialDates = () => {
        const todayDate = new Date();
        const startDate = new Date(campaignStartDate);
        const endDate = new Date(campaignEndDate);
        if (startDate < todayDate) {
            handleChangeByName('startDate', todayDate);
        } else {
            handleChangeByName('startDate', campaignStartDate);
        }
        if (endDate < todayDate) {
            handleChangeByName('endDate', todayDate);
            handleChangeByName('billingDate', todayDate);
        } else {
            handleChangeByName('endDate', campaignEndDate);
            handleChangeByName('billingDate', campaignEndDate);
        }
    }

    const handleClear = () => {
        setCurrentOffairCost(GetEmptyOffAirCost(campaign.id, returnStartDate(), returnEndDate(), returnBillingDate(), campaign.commissionRate));
        setInitialDates();
    };

    const loadOffairs = () => {
        setOffairsLoader(!offairsLoader);
    };

    return (
        <div style={pageContainerStyle}>
            <div style={createPanelStyle}>
                <CreateOffAirCost
                    readOnly={readOnly}
                    currentOffairCost={currentOffairCost}
                    offairTypes={offairTypes}
                    handleChange={handleChange}
                    handleChangeByName={handleChangeByName}
                    handleSave={handleSave}
                    handleClear={handleClear}
                    campaign={campaign}
                    billingAccountId={billingAccountId}
                    updateCurrentOffairCost={updateCurrentOffairCost}
                    campaignStartDate={campaignStartDate}
                    campaignEndDate={campaignEndDate}
                    setInitialDates={setInitialDates}
                    currentRevenueType={currentRevenueType}
                />
            </div>

            <div style={{ height: 16 }} />

            <div style={listPanelStyle}>
                <CurrentOffAirCosts
                    readOnly={readOnly}
                    offairActivities={offairActivities}
                    currentOffairCost={currentOffairCost}
                    setCurrentOffairCost={setCurrentOffairCost}
                    offairsLoader={offairsLoader}
                    loadOffairs={loadOffairs}
                    handleClear={handleClear}
                    campaign={campaign}
                    setOffairActivities={setOffairActivities}
                />
            </div>
        </div>
    );
};

export default OffAirCosts;
